"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import SiteImage from "@/components/shared/SiteImage";

// ─────────────────────────────────────────────────────────────────
//  NEWS DATA — developer edits this array to add/remove news.
//  Fields: id (string), tag, date, headline, body, image (url/path)
//  Works with 1 item or many — navigation hides when only 1 item.
// ─────────────────────────────────────────────────────────────────
export const NEWS_ITEMS = [{
  id: "01",
  tag: "Council Statement",
  date: "May 12, 2026",
  headline: "Fiji honours Girmitiya legacy with tribute to 66,000 Indian indentured labourers",
  body: "More than 140 years after thousands of men, women and children left India under indenture, Fiji is reflecting on a defining chapter of its history, one marked by hardship, resilience and enduring contribution.",
  image: "/news/Fizi-news.webp"
}, {
  id: "02",
  tag: "Heritage News",
  date: "May 9, 2026",
  headline: "India marks Girmitiya sacrifice and legacy",
  body: "India is paying tribute to the thousands of Girmitiyas whose sacrifices and hard work helped shape Fiji’s history, economy, and multicultural identity over the past 147 years.",
  image: "/news/girmit-drama.webp"
}, {
  id: "03",
  tag: "Research Update",
  date: "March 15, 2026",
  headline: "India actively working on creating comprehensive database of Girmitiya community: PM Modi",
  body: "Prime Minister Narendra Modi announced initiatives to honor the Girmitiya community, including a comprehensive database and regular conferences, strengthening ties with Trinidad and Tobago.",
  image: "/news/PmModiNews.webp"
}, {
  id: "04",
  tag: "Community Alert",
  date: "February 3, 2026",
  headline: "India working to create dedicated Girmitiya Studies Centre : EAM Jaishankar",
  body: "External Affairs Minister S Jaishankar has stated that an MoU for Cooperation between the National Archives of India and Trinidad and Tobago would help many of the people in the Caribbean country in tracing their ancestral roots and reconnecting with their families in India.",
  image: "/news/GirmitiyaMNews.webp"
}];
const INTERVAL_MS = 5000;

// Shared slide variants — both panels use the same direction logic
const slideVariants = {
  enter: dir => ({
    opacity: 0,
    y: dir > 0 ? 48 : -48
  }),
  center: {
    opacity: 1,
    y: 0
  },
  exit: dir => ({
    opacity: 0,
    y: dir > 0 ? -48 : 48
  })
};
const TRANSITION = {
  duration: 0.65,
  ease: [0.25, 0.46, 0.45, 0.94]
};

// ─────────────────────────────────────────────────────────────────
const CommunityNews = () => {
  const $ = _c(94);
  const count = NEWS_ITEMS.length;
  const [active, setActive] = useState(0);
  const [dir, setDir] = useState(1);
  const [paused, setPaused] = useState(false);
  const [progressKey, setProgressKey] = useState(0);
  const pausedRef = useRef(false);
  let t0;
  let t1;
  if ($[0] !== paused) {
    t0 = () => {
      pausedRef.current = paused;
    };
    t1 = [paused];
    $[0] = paused;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  if ($[3] !== active) {
    t2 = next => {
      const n = (next % count + count) % count;
      setDir(n > active ? 1 : -1);
      setActive(n);
      setProgressKey(_temp);
    };
    $[3] = active;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const goTo = t2;
  let t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      if (count <= 1) {
        return;
      }
      const id = setInterval(() => {
        if (pausedRef.current) {
          return;
        }
        setDir(1);
        setActive(prev => (prev + 1) % count);
        setProgressKey(_temp2);
      }, INTERVAL_MS);
      return () => clearInterval(id);
    };
    t4 = [count];
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  if (count === 0) {
    return null;
  }
  const item = NEWS_ITEMS[active];
  let t5;
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => setPaused(true);
    t6 = () => setPaused(false);
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="absolute top-0 inset-x-0 h-px bg-[#D4C4A8]" />;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="absolute inset-0 pointer-events-none opacity-30" style={{
      background: "radial-gradient(ellipse 70% 60% at 15% 50%, #f0e4cc, transparent)"
    }} />;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="flex items-center gap-2 mb-3 sm:mb-5"><div className="w-7 h-px bg-[#DF6D2A]" /><span className="font-body text-[10px] tracking-[0.25em] uppercase text-accent-readable font-light">Latest Updates</span></div>;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div>{t9}<h2 className="font-display text-[30px] sm:text-[42px] lg:text-[56px] font-light text-[#1A2332] leading-[1.05]">Community <em className="italic text-[#C9A84C]">News</em></h2></div>;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== active || $[14] !== goTo || $[15] !== progressKey) {
    t11 = count > 1 && <div className="flex items-center gap-2.5" role="tablist">{NEWS_ITEMS.map((n_0, i) => <button key={n_0.id} role="tab" aria-selected={i === active} aria-label={`News ${i + 1}`} onClick={() => goTo(i)} className="relative h-[2px] rounded-full overflow-hidden transition-all duration-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#DF6D2A]" style={{
        width: i === active ? 36 : 14,
        background: "#C4B8A4"
      }}>{i === active && <span key={progressKey} className="cn-bar-anim absolute inset-0 bg-[#DF6D2A] rounded-full" style={{
          animation: `cn-bar ${INTERVAL_MS}ms linear forwards`
        }} />}</button>)}</div>;
    $[13] = active;
    $[14] = goTo;
    $[15] = progressKey;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== t11) {
    t12 = <div className="flex flex-wrap items-end justify-between gap-5 mb-10 sm:mb-16">{t10}{t11}</div>;
    $[17] = t11;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="absolute inset-0 border border-[#C4B8A4]/60 bg-[#EDE4D6]/50 rounded-sm" style={{
      transform: "translate(7px,7px)"
    }} />;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <div className="absolute inset-0 border border-[#C4B8A4]/40 bg-[#EDE4D6]/30 rounded-sm" style={{
      transform: "translate(3.5px,3.5px)"
    }} />;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      aspectRatio: "4/3"
    };
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  const t16 = active === 0 ? "eager" : "lazy";
  let t17;
  if ($[22] !== item.headline || $[23] !== item.image || $[24] !== t16) {
    t17 = <SiteImage src={item.image} alt={item.headline} fill={true} sizes="(max-width: 1024px) 100vw, 50vw" loading={t16} className="object-cover" />;
    $[22] = item.headline;
    $[23] = item.image;
    $[24] = t16;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  let t18;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <div className="absolute inset-0 bg-gradient-to-t from-[#1A2332]/50 via-transparent to-transparent" />;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  let t19;
  if ($[27] !== active || $[28] !== dir || $[29] !== t17) {
    t19 = <motion.div key={active} custom={dir} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={TRANSITION} className="absolute inset-0">{t17}{t18}</motion.div>;
    $[27] = active;
    $[28] = dir;
    $[29] = t17;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  let t20;
  if ($[31] !== dir || $[32] !== t19) {
    t20 = <AnimatePresence mode="wait" custom={dir}>{t19}</AnimatePresence>;
    $[31] = dir;
    $[32] = t19;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  let t21;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <div className="absolute inset-[10px] border border-white/20 pointer-events-none z-10" />;
    $[34] = t21;
  } else {
    t21 = $[34];
  }
  const t22 = active + "-tag";
  let t23;
  let t24;
  let t25;
  let t26;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = {
      opacity: 0,
      x: -8
    };
    t24 = {
      opacity: 1,
      x: 0
    };
    t25 = {
      opacity: 0,
      x: 8
    };
    t26 = {
      duration: 0.35
    };
    $[35] = t23;
    $[36] = t24;
    $[37] = t25;
    $[38] = t26;
  } else {
    t23 = $[35];
    t24 = $[36];
    t25 = $[37];
    t26 = $[38];
  }
  let t27;
  if ($[39] !== item.tag || $[40] !== t22) {
    t27 = <div className="absolute top-4 left-4 z-20"><AnimatePresence mode="wait"><motion.span key={t22} initial={t23} animate={t24} exit={t25} transition={t26} className="font-body text-[9px] tracking-[0.22em] uppercase bg-[#DF6D2A] text-white px-3 py-1">{item.tag}</motion.span></AnimatePresence></div>;
    $[39] = item.tag;
    $[40] = t22;
    $[41] = t27;
  } else {
    t27 = $[41];
  }
  const t28 = String(active + 1);
  let t29;
  if ($[42] !== t28) {
    t29 = t28.padStart(2, "0");
    $[42] = t28;
    $[43] = t29;
  } else {
    t29 = $[43];
  }
  let t30;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = String(count).padStart(2, "0");
    $[44] = t30;
  } else {
    t30 = $[44];
  }
  let t31;
  if ($[45] !== t29) {
    t31 = <div className="absolute bottom-4 right-5 z-20"><span className="font-display text-[13px] sm:text-[16px] text-[#C9A84C] italic">{t29} / {t30}</span></div>;
    $[45] = t29;
    $[46] = t31;
  } else {
    t31 = $[46];
  }
  let t32;
  if ($[47] !== t20 || $[48] !== t27 || $[49] !== t31) {
    t32 = <div className="relative">{t13}{t14}<div className="relative overflow-hidden border border-[#C4B8A4] bg-[#E8DDD0] shadow-[0_16px_48px_rgba(26,35,50,0.12)]" style={t15}>{t20}{t21}{t27}{t31}</div></div>;
    $[47] = t20;
    $[48] = t27;
    $[49] = t31;
    $[50] = t32;
  } else {
    t32 = $[50];
  }
  let t33;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <div className="absolute inset-0 border border-[#C4B8A4]/60 bg-white/60 rounded-sm" style={{
      transform: "translate(7px,7px)"
    }} />;
    $[51] = t33;
  } else {
    t33 = $[51];
  }
  let t34;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <div className="absolute inset-0 border border-[#C4B8A4]/40 bg-white/40 rounded-sm" style={{
      transform: "translate(3.5px,3.5px)"
    }} />;
    $[52] = t34;
  } else {
    t34 = $[52];
  }
  let t35;
  let t36;
  let t37;
  let t38;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <div className="absolute top-0 left-0 w-10 h-[2px] bg-[#DF6D2A]" />;
    t36 = <div className="absolute top-0 left-0 w-[2px] h-10 bg-[#DF6D2A]" />;
    t37 = <div className="absolute bottom-0 right-0 w-10 h-[2px] bg-[#C9A84C]/50" />;
    t38 = <div className="absolute bottom-0 right-0 w-[2px] h-10 bg-[#C9A84C]/50" />;
    $[53] = t35;
    $[54] = t36;
    $[55] = t37;
    $[56] = t38;
  } else {
    t35 = $[53];
    t36 = $[54];
    t37 = $[55];
    t38 = $[56];
  }
  const t39 = active + "-text";
  let t40;
  if ($[57] !== item.tag) {
    t40 = <span className="font-body text-[9px] sm:text-[10px] tracking-[0.22em] uppercase text-accent-readable font-medium">{item.tag}</span>;
    $[57] = item.tag;
    $[58] = t40;
  } else {
    t40 = $[58];
  }
  let t41;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <span className="w-1 h-1 rounded-full bg-[#C4B8A4]" />;
    $[59] = t41;
  } else {
    t41 = $[59];
  }
  let t42;
  if ($[60] !== item.date) {
    t42 = <span className="font-body text-[9px] sm:text-[10px] tracking-[0.15em] uppercase text-[#8A7F74] font-light">{item.date}</span>;
    $[60] = item.date;
    $[61] = t42;
  } else {
    t42 = $[61];
  }
  let t43;
  if ($[62] !== t40 || $[63] !== t42) {
    t43 = <div className="flex flex-wrap items-center gap-2 sm:gap-3">{t40}{t41}{t42}</div>;
    $[62] = t40;
    $[63] = t42;
    $[64] = t43;
  } else {
    t43 = $[64];
  }
  let t44;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <div className="w-10 h-px bg-[#C9A84C]" />;
    $[65] = t44;
  } else {
    t44 = $[65];
  }
  let t45;
  if ($[66] !== item.headline) {
    t45 = <h3 className="font-display text-[20px] sm:text-[26px] lg:text-[32px] xl:text-[36px] font-light text-[#1A2332] leading-[1.2]">{item.headline}</h3>;
    $[66] = item.headline;
    $[67] = t45;
  } else {
    t45 = $[67];
  }
  let t46;
  if ($[68] !== item.body) {
    t46 = <p className="font-body text-[13px] sm:text-[14px] lg:text-[15px] text-[#5A6473] font-light leading-relaxed">{item.body}</p>;
    $[68] = item.body;
    $[69] = t46;
  } else {
    t46 = $[69];
  }
  let t47;
  if ($[70] !== dir || $[71] !== t39 || $[72] !== t43 || $[73] !== t45 || $[74] !== t46) {
    t47 = <motion.div key={t39} custom={dir} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={TRANSITION} className="p-6 sm:p-8 lg:p-10 flex flex-col gap-4 sm:gap-5 h-full">{t43}{t44}{t45}{t46}</motion.div>;
    $[70] = dir;
    $[71] = t39;
    $[72] = t43;
    $[73] = t45;
    $[74] = t46;
    $[75] = t47;
  } else {
    t47 = $[75];
  }
  let t48;
  if ($[76] !== dir || $[77] !== t47) {
    t48 = <div className="relative flex-1 border border-[#C4B8A4] bg-white shadow-[0_16px_48px_rgba(26,35,50,0.08)] overflow-hidden">{t35}{t36}{t37}{t38}<AnimatePresence mode="wait" custom={dir}>{t47}</AnimatePresence></div>;
    $[76] = dir;
    $[77] = t47;
    $[78] = t48;
  } else {
    t48 = $[78];
  }
  let t49;
  if ($[79] !== active || $[80] !== goTo) {
    t49 = count > 1 && <div className="flex items-center gap-3 mt-5 sm:mt-6 relative z-10"><button onClick={() => goTo(active - 1)} aria-label="Previous news" className="w-9 h-9 sm:w-11 sm:h-11 border border-[#C4B8A4] flex items-center justify-center text-[#8A7F74] hover:border-[#DF6D2A] hover:text-[#DF6D2A] transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#DF6D2A] bg-white"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M9 2L4 7L9 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg></button><button onClick={() => goTo(active + 1)} aria-label="Next news" className="w-9 h-9 sm:w-11 sm:h-11 border border-[#C4B8A4] flex items-center justify-center text-[#8A7F74] hover:border-[#DF6D2A] hover:text-[#DF6D2A] transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#DF6D2A] bg-white"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M5 2L10 7L5 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg></button><span className="font-body text-[10px] tracking-[0.2em] uppercase text-[#8A7F74] ml-1">{String(active + 1).padStart(2, "0")} / {String(count).padStart(2, "0")}</span></div>;
    $[79] = active;
    $[80] = goTo;
    $[81] = t49;
  } else {
    t49 = $[81];
  }
  let t50;
  if ($[82] !== t48 || $[83] !== t49) {
    t50 = <div className="relative flex flex-col">{t33}{t34}{t48}{t49}</div>;
    $[82] = t48;
    $[83] = t49;
    $[84] = t50;
  } else {
    t50 = $[84];
  }
  let t51;
  if ($[85] !== t32 || $[86] !== t50) {
    t51 = <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 xl:gap-16 items-stretch">{t32}{t50}</div>;
    $[85] = t32;
    $[86] = t50;
    $[87] = t51;
  } else {
    t51 = $[87];
  }
  let t52;
  if ($[88] !== t12 || $[89] !== t51) {
    t52 = <div className="relative z-10 max-w-[1300px] mx-auto px-4 sm:px-6 lg:px-10 py-14 sm:py-20 lg:py-28">{t12}{t51}</div>;
    $[88] = t12;
    $[89] = t51;
    $[90] = t52;
  } else {
    t52 = $[90];
  }
  let t53;
  if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = <div className="absolute bottom-0 inset-x-0 h-px bg-[#D4C4A8]" />;
    $[91] = t53;
  } else {
    t53 = $[91];
  }
  let t54;
  if ($[92] !== t52) {
    t54 = <><section className="font-body relative overflow-hidden bg-[#F7F1E8]" onMouseEnter={t5} onMouseLeave={t6} aria-label="Community News">{t7}{t8}{t52}{t53}</section></>;
    $[92] = t52;
    $[93] = t54;
  } else {
    t54 = $[93];
  }
  return t54;
};
export default CommunityNews;
function _temp(k) {
  return k + 1;
}
function _temp2(k_0) {
  return k_0 + 1;
}